// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useState, useEffect } from 'react';
import { z } from 'zod';
import { Container, Box, Typography, TextField, Button } from '@mui/material';
import Copyright from '../components/Copyright';
import { useBranding } from '../contexts/BrandingContext';
import { useLocalStorage } from '../contexts/LocalStorageContext';
import { useUserState } from '../contexts/UserContext';
import { useConfig } from '../hooks/other/useConfig';

const LowerCaseAlpha = z
  .string()
  .refine((value) => /^[a-z]+$/.test(value), 'Name contains lowercase alphabet');

const Email = z.string().email();

function validate(e: string) {
  const lowerCaseAlpha = LowerCaseAlpha.safeParse(e);
  const email = Email.safeParse(e);
  return lowerCaseAlpha.success || email.success;
}

const Login: React.FC = () => {
  const config = useConfig();
  const [userId, setUserId] = useState<string>('');
  const { login, isSelfSignedToken, connect } = useUserState();
  const { branding } = useBranding();
  const isDark = !!useLocalStorage()?.localStorageState?.isDark;
  const { enableDappLogin } = config.app;
  const darkLoginLogo = branding?.loginLogoDark || branding.loginLogo;
  const loginLogo = isDark ? darkLoginLogo : branding.loginLogo;
  const [inputValidated, setInputValidated] = useState(false);
  const [showUserIdAsTitle] = useState(false);

  const loginKey = (key: string) => {
    if (key === 'Enter') {
      login(userId);
    }
  };

  // If you want to display the user id in the title, e.g., for demo purposes, simply set the
  // showUserIdAsTitle state to true above.
  useEffect(() => {
    if (userId && showUserIdAsTitle) {
      document.title = userId.toUpperCase();
    }
  }, [userId, showUserIdAsTitle]);

  return (
    <Container
      maxWidth="xl"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        height: '90vh',
      }}
    >
      <Container
        maxWidth="xs"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '90vh',
          pl: 50,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {loginLogo}
          <Typography component="h1" variant="h4" sx={{ mt: 5 }}>
            {config.branding.app_name || 'Utility'}
          </Typography>
          {!enableDappLogin && (
            <Box sx={{ mt: 1 }}>
              {isSelfSignedToken() ? (
                <>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="login-userid-input"
                    placeholder="User Id"
                    name="userid"
                    autoComplete="username"
                    autoFocus
                    onChange={(event) => {
                      setInputValidated(validate(event.target.value));
                      setUserId(event.target.value);
                    }}
                    onKeyDown={(event) => loginKey(event.key)}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    disabled={!inputValidated}
                    sx={{ mt: 3, mb: 2 }}
                    id="login-button"
                    onClick={() => login(userId)}
                  >
                    Log In
                  </Button>
                </>
              ) : (
                <Button
                  type="submit"
                  fullWidth
                  sx={{ mt: 3, mb: 2 }}
                  id="login-button"
                  autoFocus
                  onClick={() => login('')}
                >
                  Log In
                </Button>
              )}
            </Box>
          )}
          {enableDappLogin && (
            <Button sx={{ m: 2 }} variant="contained" fullWidth onClick={connect}>
              Connect
            </Button>
          )}
          <Copyright />
        </Box>
      </Container>
    </Container>
  );
};

export default Login;
